import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Copy, Info, AlertOctagon, Loader2, Play, Terminal, X, Zap } from "lucide-react";

/**
 * AdminCoverageHealth — admin-only "field-fill rate" dashboard.
 *
 * Answers the question "where should the next coverage wave go?" by
 * showing every country's degree count and the % of degrees with each
 * editorial field populated. Each cell links to a suggested
 * `course_coverage_wave` trigger command so the on-call editor can
 * copy → paste → ship the next wave without leaving the page.
 */
export default function AdminCoverageHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showUntagged, setShowUntagged] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [mismatches, setMismatches] = useState(null);
  const [tailJobId, setTailJobId] = useState(null);
  const [weeklyBusy, setWeeklyBusy] = useState(false);

  useEffect(() => {
    setErr(null);
    setData(null);
    api
      .get(`/admin/coverage-health?include_untagged=${showUntagged}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, [showUntagged]);

  // Poll the jobs list every 5s so running rows update their status
  // without a manual refresh.
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const r = await api.get("/admin/coverage-health/jobs?limit=8");
        if (!cancelled) setJobs(r.data.jobs || []);
      } catch { /* silent */ }
    }
    tick();
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Load deadline mismatches once — refreshed after a wave completes.
  useEffect(() => {
    api.get("/admin/coverage-health/deadline-mismatches?limit=25")
       .then((r) => setMismatches(r.data))
       .catch(() => setMismatches({ rows: [], totals: {} }));
  }, [jobs.length]);

  async function fireWeeklyOps() {
    if (!window.confirm("Fire the weekly ops run? This will trigger coverage + deadline + verifier + US-depth waves for the 3 weakest countries.")) {
      return;
    }
    setWeeklyBusy(true);
    try {
      const r = await api.post("/admin/coverage-health/weekly-ops", {});
      toast.success(`Weekly ops fired · ${r.data.fired.length} waves started`);
      const j = await api.get("/admin/coverage-health/jobs?limit=8");
      setJobs(j.data.jobs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weekly ops failed");
    } finally {
      setWeeklyBusy(false);
    }
  }

  async function fireWave(kind, extra = {}) {
    setBusy(`${kind}-${extra.country || "any"}`);
    try {
      const r = await api.post("/admin/coverage-health/trigger",
                                { kind, ...extra });
      toast.success(`Wave started · job ${r.data.job_id}`);
      // Refresh jobs strip immediately.
      const j = await api.get("/admin/coverage-health/jobs?limit=8");
      setJobs(j.data.jobs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trigger failed");
    } finally {
      setBusy(null);
    }
  }

  if (err) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="border border-rose-200 bg-rose-50 rounded-lg p-4 flex items-start gap-2">
          <AlertOctagon className="w-4 h-4 mt-0.5 text-rose-700 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-rose-900">
              Couldn't load coverage health
            </div>
            <div className="text-sm text-rose-800 mt-0.5">{err}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-6xl mx-auto p-8 flex items-center gap-2 text-stone-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading coverage snapshot…
      </div>
    );
  }

  const { countries, totals, tracked_fields } = data;

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#4C1D95]"
      >
        <ArrowLeft className="w-3 h-3" /> Admin
      </Link>

      <h1 className="font-editorial text-4xl mt-2" data-testid="coverage-health-heading">
        Coverage health
      </h1>
      <p className="text-sm text-stone-600 mt-1 max-w-2xl">
        Degree counts and editorial fill-rates per country. Aim the next
        coverage wave at the row with the lowest composite %.
      </p>

      {/* Header strip — cross-cut totals + weakest country pointer */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          label="Total courses"
          value={totals.degrees.toLocaleString()}
          hint={totals.universities != null ? `${totals.universities.toLocaleString()} unis` : ""}
          testid="coverage-total-degrees"
        />
        <Stat label="Countries" value={totals.countries} testid="coverage-total-countries" />
        <Stat
          label="Weakest country"
          value={totals.weakest_country || "—"}
          hint={totals.weakest_pct != null ? `${totals.weakest_pct}% fill` : ""}
          testid="coverage-weakest"
        />
        <label className="flex items-end gap-2 text-xs text-stone-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showUntagged}
            onChange={(e) => setShowUntagged(e.target.checked)}
            className="w-4 h-4"
            data-testid="coverage-toggle-untagged"
          />
          Include untagged (Scorecard imports)
        </label>
      </div>

      {/* Global verifier + weekly ops row */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={fireWeeklyOps}
          disabled={weeklyBusy}
          data-testid="coverage-fire-weekly-ops"
          className={
            "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border-2 border-[#4C1D95] " +
            (weeklyBusy
              ? "bg-white text-[#4C1D95] opacity-60 cursor-wait"
              : "bg-[#4C1D95] text-white hover:bg-[#6D28D9]")
          }
        >
          {weeklyBusy
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Zap className="w-4 h-4" />}
          Weekly ops run
        </button>
        <FireBtn
          label="verify all deadlines · 100"
          onClick={() => fireWave("deadline_verifier", { limit: 100 })}
          busy={busy === "deadline_verifier-any"}
          testid="coverage-fire-verify-all"
        />
        <span className="text-[11px] text-stone-500 flex-1 min-w-[240px]">
          Weekly ops fires coverage + deadline + verifier waves against the 3
          weakest countries. Every wave writes to <code>wave_jobs</code>.
        </span>
      </div>

      {/* Recent waves strip — polls every 5s */}
      {jobs.length > 0 && (
        <section className="mt-6" data-testid="coverage-jobs-strip">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-2 flex items-center gap-1.5">
            <Terminal className="w-3 h-3" /> Recent waves ({jobs.length})
          </div>
          <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 bg-white">
            {jobs.map((j) => (
              <button
                key={j.job_id}
                type="button"
                onClick={() => setTailJobId(j.job_id)}
                className="w-full px-3 py-2 flex items-center gap-3 text-xs text-left hover:bg-stone-50 transition-colors"
                data-testid={`coverage-job-${j.job_id}`}
              >
                <StatusPill status={j.status} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono truncate text-stone-800">
                    {j.kind} · {j.country || "all"}
                    {j.subjects && ` · ${j.subjects}`}
                    {j.limit && ` · limit ${j.limit}`}
                  </div>
                  <div className="text-[10px] text-stone-500">
                    {new Date(j.started_at).toLocaleString()} · by {j.triggered_by || "—"}
                  </div>
                </div>
                {j.status === "running" && (
                  <span className="text-[10px] font-mono text-emerald-700">
                    PID {j.pid}
                  </span>
                )}
                <Terminal className="w-3 h-3 text-stone-400" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Table */}
      <div className="mt-6 border border-stone-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="coverage-health-table">
          <thead className="bg-stone-50 text-[10px] font-underground-sc tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-right px-3 py-2" title="Distinct universities in this country that appear in our degrees collection">
                Unis
              </th>
              <th className="text-right px-3 py-2" title="Undergraduate courses (degrees) in this country in our DB, and their share of our tagged catalogue">
                Courses · share
              </th>
              <th className="text-right px-3 py-2">Composite</th>
              {tracked_fields.map((f) => (
                <th key={f} className="text-right px-3 py-2">{f}</th>
              ))}
              <th className="text-left px-3 py-2">Suggested trigger</th>
            </tr>
          </thead>
          <tbody>
            {countries.map((c) => (
              <CountryRow
                key={c.country}
                row={c}
                fields={tracked_fields}
                onFire={fireWave}
                busy={busy}
              />
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-stone-500 max-w-2xl flex items-start gap-2">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          <strong>Unis</strong> = distinct universities in this country in our
          degrees collection. <strong>Courses</strong> = undergraduate courses
          (degrees) tagged with this country; the small % beside it is the
          share of our tagged catalogue only — <em>not</em> a claim about the
          country's real universe of courses.
          <strong className="ml-1">Composite %</strong> is the mean of per-field
          fill %. A country with 6 fields at 60% ranks below one with 5 fields
          at 90% — irrespective of course count.
          <strong className="ml-1">Flavour pill</strong> shows how the rows
          were sourced: <em>Comprehensive</em> = national ingester supplies
          most rows; <em>Wave-scraped</em> = AI-scrape pipeline crawled the
          country's top universities (mix of local-language + English pages);
          <em>English-taught</em> = source feed only covers English-taught /
          international programmes so local-language courses are missing;
          <em>Sparse</em> = small manual seeds only.
        </span>
      </p>

      {/* Deadline mismatches — rendered under the coverage table so
          admins can eyeball the highest-delta rows in the same view. */}      {mismatches && mismatches.rows?.length > 0 && (
        <section className="mt-10" data-testid="coverage-mismatch-panel">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-editorial text-2xl text-rose-800">
              Deadline mismatches ({mismatches.totals?.mismatches || 0})
            </h2>
            <span className="text-xs text-stone-500">
              {mismatches.totals?.matches || 0} matches ·
              {" "}{mismatches.totals?.no_second_source || 0} without 2nd source
            </span>
          </div>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">
            Every row is a degree where the wave-captured deadline diverges
            from a second-source calendar page by more than 30 days.
            Prioritise by delta descending — the biggest gaps are most likely
            to be wrong.
          </p>
          <div className="mt-3 border border-stone-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] font-underground-sc tracking-widest text-stone-500">
                <tr>
                  <th className="text-left px-3 py-2">Δ days</th>
                  <th className="text-left px-3 py-2">Country</th>
                  <th className="text-left px-3 py-2">Degree</th>
                  <th className="text-right px-3 py-2">Stored</th>
                  <th className="text-right px-3 py-2">2nd source</th>
                  <th className="text-left px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.rows.map((r) => (
                  <tr key={r.degree_id} className="border-t border-stone-100 hover:bg-stone-50"
                       data-testid={`mismatch-${r.degree_id}`}>
                    <td className="px-3 py-2 font-mono text-rose-700 font-semibold tabular-nums">
                      {r.delta_days}
                    </td>
                    <td className="px-3 py-2 text-stone-700">{r.country}</td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/degree/${r.degree_id}`}
                        className="text-[#4C1D95] hover:underline text-xs font-mono"
                      >
                        {r.degree_id}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{r.stored_deadline}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums text-rose-700">
                      <a href={r.second_source_url} target="_blank" rel="noreferrer"
                         className="hover:underline">{r.second_deadline}</a>
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-600 max-w-xs truncate"
                        title={r.notes}>{r.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tailJobId && <JobTailModal jobId={tailJobId} onClose={() => setTailJobId(null)} />}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    starting: "bg-stone-100 text-stone-700",
    running:  "bg-amber-100 text-amber-800",
    ok:       "bg-emerald-100 text-emerald-800",
    failed:   "bg-rose-100 text-rose-800",
    crashed:  "bg-rose-100 text-rose-800",
  };
  const cls = map[status] || "bg-stone-100 text-stone-700";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-underground-sc tracking-widest ${cls}`}
      data-testid={`coverage-job-status-${status}`}
    >
      {status}
    </span>
  );
}

/**
 * JobTailModal — live-tails a wave's log file via the
 * /admin/coverage-health/jobs/{id}/tail endpoint. Polls every 2s
 * while open so a Monday-morning admin can watch a coverage wave
 * process rows in real time without leaving the dashboard.
 */
function JobTailModal({ jobId, onClose }) {
  const [tail, setTail] = useState("");
  const [note, setNote] = useState("");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const [tailRes, jobsRes] = await Promise.all([
          api.get(`/admin/coverage-health/jobs/${jobId}/tail?lines=80`),
          api.get(`/admin/coverage-health/jobs?limit=20`),
        ]);
        if (cancelled) return;
        setTail(tailRes.data.tail || "");
        setNote(tailRes.data.note || "");
        const found = (jobsRes.data.jobs || []).find((j) => j.job_id === jobId);
        setMeta(found || null);
      } catch (e) {
        if (!cancelled) setNote(e?.response?.data?.detail || "log unavailable");
      }
    }
    tick();
    const t = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [jobId]);

  const isDone = meta && ["ok", "failed", "crashed"].includes(meta.status);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="job-tail-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-stone-200 flex items-center gap-3">
          {meta && <StatusPill status={meta.status} />}
          <div className="flex-1 min-w-0">
            <div className="font-mono text-sm truncate text-stone-900" data-testid="job-tail-title">
              {meta
                ? `${meta.kind} · ${meta.country || "all"} · ${meta.job_id}`
                : `Loading ${jobId}…`}
            </div>
            <div className="text-[11px] text-stone-500 truncate">
              {meta?.argv?.join(" ")}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-stone-100"
            data-testid="job-tail-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <pre
          className="flex-1 overflow-auto bg-stone-950 text-stone-100 text-[11px] font-mono p-4 whitespace-pre-wrap"
          data-testid="job-tail-content"
        >
          {tail || note || "waiting for log…"}
        </pre>

        <footer className="px-5 py-2 border-t border-stone-200 text-[11px] text-stone-500 flex items-center gap-3">
          <span>{isDone ? "Job complete" : "Live · polling every 2s"}</span>
          {meta?.pid && <span>PID {meta.pid}</span>}
          {meta?.return_code != null && <span>rc {meta.return_code}</span>}
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
        {label}
      </div>
      <div className="mt-1 font-editorial text-2xl leading-none tabular-nums text-stone-900">
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[11px] text-stone-500">{hint}</div>}
    </div>
  );
}

const FLAVOUR_META = {
  comprehensive: {
    label: "Comprehensive",
    cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
    hint: "A national ingester supplies most rows — coverage broadly matches this country's real course universe.",
  },
  wave_scraped: {
    label: "Wave-scraped",
    cls: "bg-indigo-50 text-indigo-800 border-indigo-200",
    hint: "Coverage comes from the AI-scrape wave pipeline — mix of local-language and English pages from the country's top universities. Wide but not exhaustive; a proper national ingester would take it to Comprehensive.",
  },
  english_taught: {
    label: "English-taught",
    cls: "bg-amber-50 text-amber-900 border-amber-200",
    hint: "Rows come from an English-taught / international-programme feed. Local-language degrees are NOT ingested — genuine limitation of the source.",
  },
  sparse: {
    label: "Sparse",
    cls: "bg-stone-100 text-stone-700 border-stone-300",
    hint: "Small manual seeds only — no comprehensive national ingester wired yet. Consider firing a coverage wave.",
  },
};

function FlavourPill({ flavour, source }) {
  const meta = FLAVOUR_META[flavour] || FLAVOUR_META.sparse;
  return (
    <span
      className={`inline-block px-1.5 py-0.5 text-[10px] font-underground-sc tracking-wider rounded border ${meta.cls}`}
      title={`${meta.hint}  Top source: ${source || "—"}`}
      data-testid={`coverage-flavour-pill`}
    >
      {meta.label}
    </span>
  );
}

function CountryRow({ row, fields, onFire, busy }) {
  const composite = row.composite_pct;
  const compCls =
    composite < 20 ? "text-rose-700 font-semibold" :
    composite < 50 ? "text-amber-700" :
    "text-emerald-700";
  const trigger = _suggestTrigger(row);
  // Prefer the explicit `degrees_n` field but fall back to `n` for
  // older cached responses so a stale bundle doesn't nuke the row.
  const degreesN = row.degrees_n ?? row.n ?? 0;
  const unisN = row.unis_n ?? 0;
  const share = row.share_of_catalogue_pct;
  return (
    <tr className="border-t border-stone-100 hover:bg-stone-50" data-testid={`coverage-row-${row.country}`}>
      <td className="px-3 py-2 font-medium text-stone-900">
        <div className="flex items-center gap-2 flex-wrap">
          <span>{row.country}</span>
          {row.coverage_flavour && (
            <FlavourPill
              flavour={row.coverage_flavour}
              source={row.coverage_flavour_source}
            />
          )}
        </div>
      </td>
      <td
        className="px-3 py-2 text-right tabular-nums text-stone-700"
        data-testid={`coverage-unis-${row.country}`}
        title={`${unisN.toLocaleString()} distinct universities in ${row.country} present in our degrees collection`}
      >
        {unisN.toLocaleString()}
      </td>
      <td
        className="px-3 py-2 text-right tabular-nums text-stone-700"
        data-testid={`coverage-courses-${row.country}`}
        title={`${degreesN.toLocaleString()} undergraduate courses (degrees) tagged as ${row.country}. Share = fraction of our tagged catalogue — not a claim about the country's real universe.`}
      >
        <span>{degreesN.toLocaleString()}</span>
        {typeof share === "number" && (
          <span className="ml-1 text-[10px] text-stone-400 font-mono">
            · {share}%
          </span>
        )}
      </td>
      <td className={`px-3 py-2 text-right tabular-nums ${compCls}`}>{composite}%</td>
      {fields.map((f) => {
        const cell = row.fields[f] || { pct: 0, filled: 0 };
        const cls =
          cell.pct < 20 ? "bg-rose-50 text-rose-800" :
          cell.pct < 50 ? "bg-amber-50 text-amber-900" :
          "bg-emerald-50 text-emerald-900";
        return (
          <td
            key={f}
            className={`px-3 py-2 text-right tabular-nums text-xs ${cls}`}
            title={`${cell.filled}/${degreesN}`}
            data-testid={`coverage-cell-${row.country}-${f}`}
          >
            {cell.pct}%
          </td>
        );
      })}
      <td className="px-3 py-2 whitespace-nowrap">
        {trigger ? (
          <div className="flex flex-col items-start gap-1">
            <FireBtn
              label={trigger.label}
              onClick={() => onFire(trigger.kind, trigger.args)}
              busy={busy === `${trigger.kind}-${row.country}`}
              testid={`coverage-fire-${row.country}`}
            />
            {trigger.deadlineArgs && (
              <FireBtn
                label="deadlines only"
                subtle
                onClick={() => onFire("deadline", trigger.deadlineArgs)}
                busy={busy === `deadline-${row.country}`}
                testid={`coverage-fire-deadline-${row.country}`}
              />
            )}
          </div>
        ) : null}
      </td>
    </tr>
  );
}

function _suggestTrigger(row) {
  const isNamed = row.country && !["?", ""].includes(row.country);
  if (!isNamed) return null;
  if (row.country === "US") {
    return {
      label: "us depth 20",
      kind: "us_depth",
      args: { limit: 20 },
      deadlineArgs: { country: "US", limit: 100 },
    };
  }
  return {
    label: "coverage 5×",
    kind: "coverage",
    args: { country: row.country, unis: 5,
             subjects: "cs,business,engineering" },
    deadlineArgs: { country: row.country, limit: 100 },
  };
}

function FireBtn({ label, onClick, busy, subtle, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={
        "inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full border transition-colors " +
        (subtle
          ? "text-stone-600 border-stone-200 hover:text-[#4C1D95] hover:border-[#4C1D95]"
          : "text-[#4C1D95] border-[#4C1D95]/40 hover:bg-[#4C1D95] hover:text-white") +
        (busy ? " opacity-60 cursor-wait" : "")
      }
    >
      {busy
        ? <Loader2 className="w-3 h-3 animate-spin" />
        : <Play className="w-3 h-3" />}
      {label}
    </button>
  );
}
